import { useCallback, useEffect, useState } from 'react';
import { usePresetChange } from 'components/organisms/resource-management/scheduler-config/listeners';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { getUserSelectablePresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import { queueMacrotask } from 'utils/macrotask';
import { SchedulerReference } from '../types';

interface Props {
  loading: boolean;
  schedulerRef: SchedulerReference;
  currentPresetId: ViewPresetId;
  onPresetChange: (id: ViewPresetId, clearEvents?: boolean) => void;
}
export const useViewPresetManager = ({
  loading,
  schedulerRef,
  currentPresetId,
  onPresetChange,
}: Props): void => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (loading || initialized) {
      return;
    }
    if (schedulerRef.current?.instance.viewPreset?.id !== currentPresetId) {
      // eslint-disable-next-line no-param-reassign
      schedulerRef.current.instance.viewPreset = currentPresetId;
    }
    setInitialized(true);
  }, [currentPresetId, schedulerRef, loading, initialized]);
  useEffect(() => {
    if (
      !initialized ||
      schedulerRef.current?.instance.viewPreset.id === currentPresetId
    ) {
      return;
    }
    queueMacrotask(() => {
      if (
        getUserSelectablePresetId(currentPresetId) !==
        getUserSelectablePresetId(schedulerRef.current.instance.viewPreset.id)
      ) {
        onPresetChange(
          currentPresetId,
          currentPresetId !== schedulerRef.current.instance.viewPreset.id,
        );
      }
      // eslint-disable-next-line no-param-reassign
      schedulerRef.current.instance.viewPreset = currentPresetId;
    });
  }, [currentPresetId, initialized, onPresetChange, schedulerRef]);
  const presetChangeHandler = useCallback(
    (params) => {
      if (!initialized || params.to.id === currentPresetId) {
        return;
      }
      onPresetChange(params.to.id as ViewPresetId);
    },
    [currentPresetId, initialized, onPresetChange],
  );

  usePresetChange(schedulerRef, presetChangeHandler);
};
